import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { 
  ChevronDown, 
  ChevronRight, 
  AlertCircle, 
  Search, 
  Shield, 
  Cpu,
  Sparkles,
  FileText,
  FileJson,
  History,
  Loader2,
  ChevronLeft,
  Trash2,
  ShieldAlert
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import InvestigationGraph from "../components/InvestigationGraph";
import CredentialsTable from "../components/CredentialsTable";
import { ReactFlowProvider } from "reactflow";
import InvestigationInsights from "../components/InvestigationInsights";
import FrostedModal from "../components/modals/FrostedModal";

/* ================= STYLES ================= */
const frosted = "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

const ASSET_DESCRIPTIONS = {
  computerNames: "Infected computers where the identity was identified.",
  os: "Infected operating systems connected with target.",
  ipAddresses: "IP addresses observed in connection with compromised devices.",
  countries: "Geographical locations inferred from compromised systems.",
  relatedAccounts: "Additional accounts discovered on the same compromised devices.",
  passwords: "Cleartext passwords recovered from infected systems.",
};

/* ================= HELPERS ================= */
function groupItemsByCount(dataArray, key) {
  const map = new Map();
  if (!Array.isArray(dataArray)) return [];
  dataArray.forEach((entry) => {
    const value = key ? entry?.[key] : entry;
    if (!value || (typeof value === "string" && !value.trim())) return;
    const label = String(value).trim();
    map.set(label, (map.get(label) || 0) + 1);
  });
  return Array.from(map.entries());
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch (e) { return dateString; }
}

/* ================= COMPONENTS ================= */
function LoadingOverlay({ message }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#0a1224]/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-2 border-sky-500/20" />
          <div className="absolute inset-0 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
        </div>
        <p className="text-sm text-neutral-400 animate-pulse">{message}</p>
      </div>
    </div>
  );
}

function EnterprisePaywall() {
  const navigate = useNavigate();
  const frosted =
    "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

  return (
    <div className="w-full px-4 py-8">
      <div className="max-w-2xl mx-auto mt-12">
        <div className={`rounded-md ${frosted} p-8 text-center`}>
          <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
            <Sparkles className="h-6 w-6 text-sky-300" />
          </div>
          <h2 className="text-xl font-semibold text-white">
            Investigation+ is an upgraded feature
          </h2>
          <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
            AI-driven identity attribution, neural risk scoring, and forensic
            footprinting are available on the{" "}
            <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
            plan, or as an add-on to{" "}
            <span className="text-sky-200 font-medium">Enterprise</span>.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/upgrades")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
            >
              View upgrade options
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              Back to dashboard
            </button>
          </div>
          <div className="mt-6 pt-5 border-t border-white/5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[10px] font-bold uppercase tracking-[0.3em]">
            <a
              href="https://knowledge.whiteintel.io/white-intel-usage/investigations+-beta"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-neutral-500 hover:text-sky-400 transition-colors"
            >
              <FileText size={12} /> Documentation
            </a>
            <a
              href="https://blog.whiteintel.io/exposing-threat-actors-with-whiteintel-ai/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-neutral-500 hover:text-sky-400 transition-colors"
            >
              <FileText size={12} /> Sample Use Case
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetGroup({ title, description, count, items }) {
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(15);
  if (!items || items.length === 0) return null;
  const visible = items.slice(0, limit);

  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/30 overflow-hidden mb-3">
      <button onClick={() => setOpen(!open)} className="w-full text-left px-3 py-2 bg-neutral-900/60 hover:bg-neutral-800/40 transition-colors">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-white">{title} <span className="text-neutral-500 font-mono">({count})</span></span>
          {open ? <ChevronDown className="h-4 w-4 text-neutral-500" /> : <ChevronRight className="h-4 w-4 text-neutral-500" />}
        </div>
        <p className="mt-1 text-[10px] text-neutral-500 leading-tight">{description}</p>
      </button>
      {open && (
        <div className="max-h-60 overflow-y-auto custom-scrollbar border-t border-neutral-800">
          {visible.map(([label, count], i) => (
            <div key={i} className="flex justify-between px-3 py-2 text-[11px] hover:bg-white/5 border-b border-white/5 last:border-0">
              <span className="truncate pr-4 text-neutral-300 font-mono">{label}</span>
              <span className="text-sky-400 font-mono">{count}</span>
            </div>
          ))}
          {items.length > limit && (
            <button 
              onClick={(e) => { e.stopPropagation(); setLimit(prev => prev + 25); }}
              className="w-full py-2 text-[10px] font-bold uppercase tracking-widest text-sky-400 bg-sky-500/5 hover:bg-sky-500/10 transition-colors"
            >
              Load More Results ({items.length - limit} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ================= MAIN PAGE ================= */
export default function InvestigationsPage() {
  const [deleteModal, setDeleteModal] = useState({ open: false, jobId: null, target: "" });
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Results");
  const [headerSearch, setHeaderSearch] = useState("");
  const [recentSearches, setRecentSearches] = useState([]);
  const [investigationData, setInvestigationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [appLimit, setAppLimit] = useState(30);
  const [fetchingHistory, setFetchingHistory] = useState(true);
  const [localError, setLocalError] = useState(null);
  
  // Job Polling States
  const [jobId, setJobId] = useState(null);
  const [workerStatus, setWorkerStatus] = useState({
    summary: 'pending',
    credentials: 'pending',
    graph: 'pending',
    ai_analysis: 'pending'
  });

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get("query");
  const urlJobId = queryParams.get("job_id");

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const getJwtToken = () => localStorage.getItem("token") || "";

  const openDeleteConfirm = (e, job) => {
    e.stopPropagation(); // Prevent navigating to the job
    setDeleteModal({ open: true, jobId: job.job_uuid, target: job.target_email });
  };

  const handleConfirmDelete = async () => {
    const id = deleteModal.jobId;
    try {
      const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/delete_job.php", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${getJwtToken()}` 
        },
        body: JSON.stringify({ job_id: id }),
      });
      
      const json = await res.json();
      if (json.success) {
        setRecentSearches(prev => prev.filter(job => job.job_uuid !== id));
        setDeleteModal({ open: false, jobId: null, target: "" });
      } else {
        setLocalError(json.error || "Failed to delete record.");
      }
    } catch (err) {
      setLocalError("Purge failed: Neural link timeout.");
    }
  };


  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/get_history.php", {
        headers: { Authorization: `Bearer ${getJwtToken()}` }
      });
      const json = await res.json();
      if (res.status === 401) { navigate('/signin'); return; }
      if (json.success) {
        setRecentSearches(json.recent_queries);
        setFetchingHistory(false);
      }
    } catch (err) { 
      console.error(err); 
      setFetchingHistory(false);
    }
  }, [navigate]);

  useEffect(() => {
    const verify = async () => {
      setCheckingAccess(true);
      try {
        const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/check_access.php", {
          headers: { Authorization: `Bearer ${getJwtToken()}` }
        });
        const json = await res.json();
        if (res.status === 401) { navigate('/signin'); return; }
        const ok = json.success && json.subscription === 'tifirm';
        setHasAccess(ok);
        if (ok) fetchHistory();
      } catch (err) { setHasAccess(false); }
      finally { setCheckingAccess(false); }
    };
    verify();
  }, [fetchHistory, navigate]);

  // --- 1. START OR RESUME INVESTIGATION JOB ---
  useEffect(() => {
    if (!query || !hasAccess) {
        setInvestigationData(null);
        setJobId(null);
        return;
    }
    const initInvestigation = async () => {
      setInvestigationData(null);
      setLocalError(null);
      setWorkerStatus({ summary: 'pending', credentials: 'pending', graph: 'pending', ai_analysis: 'pending' });

      if (urlJobId) {
        setJobId(urlJobId);
        setLoading(true);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/submit_job.php", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
          body: JSON.stringify({ query }),
        });
        
        const json = await res.json();
        if (res.status === 401) { navigate('/signin'); return; }
        
        if (json.success) {
          setJobId(json.job_id);
        } else {
          setLocalError(json.error);
          setLoading(false);
        }
      } catch (err) { 
        setLocalError("Neural uplink failed.");
        setLoading(false); 
      }
    };
    initInvestigation();
  }, [query, urlJobId, hasAccess, navigate]);

  // --- 2. TERMINAL-AWARE POLLING ENGINE ---
  useEffect(() => {
    if (!jobId) return;

const fetchSummary = async (id) => {
    try {
        const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/get_job_summary.php", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
            body: JSON.stringify({ job_id: id }),
        });
        const json = await res.json();
        
        if (json.success) {
            // If json.data is null, setInvestigationData(null) happens.
            // But we need to distinguish between "not loaded yet" and "loaded but null".
            setInvestigationData(json.data ?? { is_empty: true }); 
            fetchHistory();
        }
    } catch (e) { 
        console.error("Summary fetch error", e); 
    }
};

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`https://whiteintel.io/api/regular/app/v2/investigations/check_status.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
          body: JSON.stringify({ job_id: jobId })
        });
        const json = await res.json();

        if (json.success) {
          setWorkerStatus(json.components);
          if (json.components.summary === 'completed' && !investigationData) {
            fetchSummary(jobId);
          }
          
          const statuses = Object.values(json.components);
          const isEverythingFinished = statuses.every(s => s === 'completed' || s === 'failed');
          
          if (isEverythingFinished) {
            setLoading(false);
            clearInterval(interval);
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [jobId, investigationData, fetchHistory]);

  const computerNames = useMemo(() => groupItemsByCount(investigationData?.history || [], "computername"), [investigationData]);
  const operatingSystems = useMemo(() => groupItemsByCount(investigationData?.history || [], "os"), [investigationData]);
  const ipAddresses = useMemo(() => groupItemsByCount(investigationData?.history || [], "ip"), [investigationData]);
  const countries = useMemo(() => groupItemsByCount(investigationData?.history || [], "country"), [investigationData]);
  const relatedAccounts = useMemo(() => groupItemsByCount(investigationData?.related_accounts || []), [investigationData]);
  const passwords = useMemo(() => groupItemsByCount(investigationData?.passwords || []), [investigationData]);
  const applications = useMemo(() => groupItemsByCount((investigationData?.normalized_subdomains || []).map(d => d.toLowerCase())), [investigationData]);
  
  // GATEKEEPER LOGIC
// This checks if the scan itself returned no forensic records
// Add this helper variable
const isInitialLoading = !investigationData && (workerStatus.summary === 'pending' || workerStatus.summary === 'processing');
const isSummaryFinished = workerStatus.summary === 'completed' || workerStatus.summary === 'failed';
const hasNoResults = workerStatus.summary === 'completed' && investigationData?.is_empty === true;

  const handleExportData = () => {
    if (!investigationData) return;
    const blob = new Blob([JSON.stringify(investigationData, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Forensic_Report_${query}.json`;
    link.click();
  };

  return (
    <div className="h-screen w-full bg-[#000414] text-white overflow-hidden">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} activeKey="investigations" />

      <div
        className="flex flex-col h-full overflow-hidden relative"
        style={{
          marginLeft: collapsed ? "56px" : "224px",
          width: `calc(100vw - ${collapsed ? "56px" : "224px"})`,
        }}
      >
        {checkingAccess && <LoadingOverlay message="Verifying Authorization..." />}
        {loading && !jobId && <LoadingOverlay message="Establishing Neural Link..." />}

        {localError && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`max-w-md w-full ${frosted} border-red-500/30 rounded-md p-6 shadow-2xl animate-in zoom-in-95`}>
              <div className="flex items-center gap-4 mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Neural Exception</h3>
              </div>
              <p className="text-sm text-neutral-400 mb-6">{localError}</p>
              <button onClick={() => { setLocalError(null); navigate('/investigations'); }} className="w-full py-3 rounded-md bg-white/5 border border-white/10 text-white text-xs font-bold uppercase tracking-widest">Return to Search</button>
            </div>
          </div>
        )}

        <header className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)] shrink-0">
          <div className="px-6 h-14 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
               {query && <button onClick={() => navigate('/investigations')} className="p-1 hover:bg-white/5 rounded text-neutral-500"><ChevronLeft className="w-5 h-5" /></button>}
              <div className="flex items-center gap-3">
                <h1 className="text-[20px] font-bold leading-tight tracking-tight flex items-baseline gap-2">
                    Investigation+
                    <span className="text-[9px] px-1.5 py-0 border border-sky-500/30 text-sky-500/80 rounded uppercase tracking-[0.15em] font-medium leading-4">Beta</span>
                </h1>
                {!query && (
                                                <div className="mt-2 p-2 bg-sky-500/5 border border-sky-500/10 rounded-lg flex items-start gap-3">
    <div className="mt-0.5 text-sky-500">
      {/* Swap 'InfoIcon' with your preferred icon library component (e.g., <Info size={18} />) */}
      <i className="fas fa-info-circle text-base"></i> 
    </div>
    
    <div className="text-[11px] leading-relaxed text-slate-400">
      <p>
        This is a beta version. AI may produce inaccuracies. 
        Usage is limited to <span className="text-slate-200 font-medium">20 investigations per hour</span> and <span className="text-slate-200 font-medium">100 per day</span>.
      </p>
    </div>
  </div>
                )}
              </div>
              
               {query && (
                <div className="hidden md:flex items-center px-3 py-1 bg-sky-500/10 border border-sky-500/20 rounded-full">
                  <span className="text-[10px] font-black uppercase text-sky-400 mr-2">Target</span>
                  <span className="text-xs font-mono text-white truncate max-w-[200px]">{query}</span>
                </div>
               )}
            </div>

            

            {query && (
              <div className="hidden lg:block relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input 
                  type="text"
                  placeholder="Pivot to new identity..."
                  value={headerSearch}
                  onChange={(e) => setHeaderSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = e.target.value.trim();
                      if (!val) return;
                      navigate(`/investigations?query=${encodeURIComponent(val)}`);
                      setHeaderSearch("");
                    }
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-md py-1.5 pl-10 pr-4 text-sm outline-none focus:border-sky-500/50 transition-all"
                />
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {!checkingAccess && !hasAccess && <EnterprisePaywall />}
          {!checkingAccess && hasAccess && (
            <>
              {!query && (
                
                <div className="max-w-6xl mx-auto animate-in fade-in zoom-in duration-700">

                  <div className="flex flex-col items-center justify-center pt-10 pb-16">
                    <Sparkles className="w-12 h-12 text-sky-400 mb-6 animate-pulse" />
                    <h2 className="text-3xl font-bold mb-2">Neural Investigation</h2>
                    <p className="text-neutral-500 mb-8 text-center max-w-md">Deploy neural modeling to uncover lateral threat vectors.</p>
                    <div className="w-full max-w-2xl relative">
                      <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-neutral-500" />
                      <input 
                        type="text" 
                        placeholder="Enter an username or email address" 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            const val = e.target.value.trim();
                            navigate(`/investigations?query=${encodeURIComponent(val)}`);
                          }
                        }}
                        className={`w-full h-16 pl-14 pr-32 rounded-md ${frosted} text-xl outline-none focus:ring-2 focus:ring-sky-500/40 shadow-2xl`} 
                      />
                    </div>
                  </div>

                  <div className="w-full min-h-[300px]">
                    <div className="flex justify-between items-end mb-4 border-b border-white/5 pb-2">
                      <h3 className="text-[10px] font-black uppercase text-neutral-500 tracking-widest flex items-center gap-2">
                        <History className="w-3 h-3"/> Analysis History
                      </h3>
                    </div>
                    {fetchingHistory ? (
                      <div className="flex flex-col items-center justify-center py-20 bg-white/[0.01] rounded-md border border-white/5">
                        <Loader2 className="w-8 h-8 text-sky-500/50 animate-spin mb-3" />
                        <p className="text-[10px] uppercase text-neutral-600 font-bold">Synchronizing...</p>
                      </div>
                    ) : recentSearches.length > 0 ? (
                      <div className="rounded-md border border-white/5 bg-white/[0.02] overflow-hidden">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-white/[0.03] text-[10px] uppercase text-neutral-500 tracking-widest">
                            <tr>
                              <th className="px-6 py-3">Target Identity</th>
                              <th className="px-6 py-3">Status</th>
                              <th className="px-6 py-3">Analyzed At</th>
                              <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
<tbody className="divide-y divide-white/5">
  {recentSearches.map(job => (
    <tr key={job.job_uuid} onClick={() => navigate(`/investigations?query=${encodeURIComponent(job.target_email)}&job_id=${job.job_uuid}`)} className="hover:bg-sky-500/5 cursor-pointer group">
      <td className="px-6 py-4 flex items-center gap-3">
        <Shield className="w-4 h-4 text-neutral-500 group-hover:text-sky-400" />
        <span className="text-sm text-neutral-300">{job.target_email}</span>
      </td>
      <td className="px-6 py-4">
        <span className="text-[9px] font-bold border border-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded uppercase">{job.status}</span>
      </td>
      <td className="px-6 py-4 text-xs text-neutral-600 font-mono">{formatDate(job.created_at)}</td>
      <td className="px-6 py-4 text-right">
        <div className="flex justify-end items-center gap-3">
<button 
              onClick={(e) => openDeleteConfirm(e, job)}
              className="p-1.5 text-neutral-600 hover:text-red-400 hover:bg-red-400/10 rounded transition-all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
        </div>
      </td>
    </tr>
  ))}
</tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 bg-white/[0.01] border-dashed border border-white/5">
                        <History className="w-8 h-8 text-neutral-800 mb-3" />
                        <p className="text-xs text-neutral-600 italic">No history found.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {query && (jobId || investigationData) && (
                <div className="animate-in fade-in duration-500">
                  {workerStatus.summary === 'completed' && hasNoResults ? (
                    /* CASE: NO INTELLIGENCE FOUND */
              <div className="flex flex-col items-center justify-center py-32 px-4 text-center">
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-red-500/5 blur-3xl rounded-full" />
          <ShieldAlert className="w-20 h-20 text-neutral-800 relative z-10" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">No Stealer Intelligence Found</h2>
        <p className="text-neutral-500 max-w-lg mx-auto text-sm leading-relaxed">
          The neural engine completed the scan for <span className="text-sky-400 font-mono">{query}</span>, but found no matches in our stealer log datalakes.
        </p>
        <button 
          onClick={() => navigate('/investigations')}
          className="mt-10 px-8 py-3 bg-white/5 border border-white/10 rounded-md text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 hover:bg-white/10 hover:text-white transition-all"
        >
          Return Back
        </button>
      </div>
                  ) : (
                    /* CASE: DATA EXISTS OR STILL PROCESSING */
                    <>
                      <div className={`rounded-md ${frosted} p-5 mb-6 flex justify-between items-end`}>
                        <div>
                          <h2 className="text-lg font-bold text-sky-400">{query}</h2>
                          <p className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Neural Identity Map</p>
                        </div>
                        <div className="flex gap-4 text-right">
                          <div><p className="text-[9px] text-neutral-500 uppercase font-black">First Seen</p><p className="text-xs font-mono">{investigationData ? formatDate(investigationData.first_seen) : "---"}</p></div>
                          <div><p className="text-[9px] text-neutral-500 uppercase font-black">Last Seen</p><p className="text-xs font-mono text-sky-400">{investigationData ? formatDate(investigationData.last_seen) : "---"}</p></div>
                        </div>
                      </div>

                      <div className="flex gap-6 mb-6 border-b border-neutral-800">
                        {["Results", "Graph", "Credentials", "AI Insights"].map(tab => (
                          <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-medium relative flex items-center gap-2 ${activeTab === tab ? "text-white" : "text-neutral-500"}`}>
                            {tab}
                            {tab === "Results" && workerStatus.summary === 'processing' && <Loader2 className="w-3 h-3 animate-spin text-sky-500" />}
                            {tab === "Graph" && workerStatus.graph === 'processing' && <Loader2 className="w-3 h-3 animate-spin text-sky-500" />}
                            {tab === "Credentials" && workerStatus.credentials === 'processing' && <Loader2 className="w-3 h-3 animate-spin text-sky-500" />}
                            {tab === "AI Insights" && workerStatus.ai_analysis === 'processing' && <Loader2 className="w-3 h-3 animate-spin text-sky-500" />}
                            {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500" />}
                          </button>
                        ))}
                      </div>

                      {activeTab === "Results" && (
                        <div className="grid grid-cols-1 xl:grid-cols-[400px_1fr] gap-6 pb-10">
                            <div className={`rounded-md ${frosted} p-4 space-y-2 h-fit min-h-[400px] relative`}>
                                <div className="flex justify-between items-center pb-2 mb-2 border-b border-white/5">
                                    <h3 className="text-xs font-bold text-neutral-500 uppercase tracking-wider">Known Assets</h3>
                                    {investigationData && (
                                        <button onClick={handleExportData} className="flex items-center gap-1.5 text-[10px] bg-white/5 text-sky-400 border border-white/10 px-2 py-1 rounded transition-colors"><FileJson className="w-3 h-3" /> Export Assets</button>
                                    )}
                                </div>
                                {(workerStatus.summary === 'pending' || workerStatus.summary === 'processing') && !investigationData ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <Loader2 className="w-6 h-6 text-sky-500/50 animate-spin mb-3" />
                                        <p className="text-[10px] uppercase text-neutral-600 font-bold tracking-tighter">Scanning Identity Assets...</p>
                                    </div>
                                ) : (
                                    <>
                                        <AssetGroup title="Computer Names" description={ASSET_DESCRIPTIONS.computerNames} count={computerNames.length} items={computerNames} />
                                        <AssetGroup title="Operating Systems" description={ASSET_DESCRIPTIONS.os} count={operatingSystems.length} items={operatingSystems} />
                                        <AssetGroup title="IP Addresses" description={ASSET_DESCRIPTIONS.ipAddresses} count={ipAddresses.length} items={ipAddresses} />
                                        <AssetGroup title="Countries" description={ASSET_DESCRIPTIONS.countries} count={countries.length} items={countries} />
                                        <AssetGroup title="Related Accounts" description={ASSET_DESCRIPTIONS.relatedAccounts} count={relatedAccounts.length} items={relatedAccounts} />
                                        <AssetGroup title="Passwords" description={ASSET_DESCRIPTIONS.passwords} count={passwords.length} items={passwords} />
                                    </>
                                )}
                            </div>

                            <div className={`rounded-md ${frosted} p-5 h-fit min-h-[400px] relative`}>
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-sm font-semibold flex items-center gap-2"><Cpu className="w-4 h-4 text-sky-400"/> Applications Footprint</h3>
                                    {investigationData && (
                                        <span className="text-xs bg-white/5 border border-white/10 px-2 py-1 rounded text-neutral-400">{applications.length} Detected</span>
                                    )}
                                </div>
                                {(workerStatus.summary === 'pending' || workerStatus.summary === 'processing') && !investigationData ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <Loader2 className="w-6 h-6 text-sky-500/50 animate-spin mb-3" />
                                        <p className="text-[10px] uppercase text-neutral-600 font-bold tracking-tighter">Mapping Application Surface...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                            {applications.slice(0, appLimit).map(([app, count]) => (
                                                <div key={app} className="flex justify-between items-center bg-white/5 border border-white/5 px-3 py-2.5 rounded-lg text-xs hover:border-sky-500/30 transition-all">
                                                    <span className="truncate text-neutral-300" title={app}>{app}</span>
                                                    <span className="text-neutral-500 font-mono ml-2">{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {applications.length > appLimit && (
                                            <button onClick={() => setAppLimit(prev => prev + 30)} className="w-full mt-6 py-2 rounded-md bg-white/5 border border-white/10 text-[10px] font-bold uppercase tracking-widest text-sky-400 hover:bg-sky-500/10 transition-all">
                                                Load More Applications (+{applications.length - appLimit})
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                      )}

                      {activeTab === "Graph" && (
                        <div className="h-[75vh] border border-neutral-800 rounded-md overflow-hidden bg-[#06101e] relative">
                            <ReactFlowProvider>
                                <InvestigationGraph jobId={jobId} status={workerStatus.graph}/>
                            </ReactFlowProvider>
                        </div>
                      )}

                      {activeTab === "Credentials" && (
                        <div className="h-[calc(100vh-250px)] animate-in fade-in duration-300">
                            <CredentialsTable jobId={jobId} status={workerStatus.credentials}/>
                        </div>
                      )}

                      {activeTab === "AI Insights" && (
                        <InvestigationInsights query={query} jobId={jobId} status={workerStatus.ai_analysis} />
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
          <FrostedModal
          open={deleteModal.open}
          onClose={() => setDeleteModal({ open: false, jobId: null, target: "" })}
          icon={<ShieldAlert className="w-8 h-8 text-red-500" />}
          title="Delete Investigation"
          description={`Are you sure you want to permanently delete the investigation for ${deleteModal.target}? This action cannot be undone.`}
          primaryAction={{
            label: "Confirm Delete",
            onClick: handleConfirmDelete
          }}
          secondaryAction={{
            label: "Cancel",
            onClick: () => setDeleteModal({ open: false, jobId: null, target: "" })
          }}
        />
        </main>
      </div>
    </div>
  );
}